import { yupResolver } from "@hookform/resolvers/yup";
import AddIcon from "@mui/icons-material/Add";
import DateRangeSharp from "@mui/icons-material/DateRangeSharp";
import EditIcon from "@mui/icons-material/Edit";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  GlobalStyles,
  IconButton,
  Stack,
  Typography,
} from "@mui/material";
import { enqueueSnackbar } from "notistack";
import { forwardRef, useEffect, useMemo, useState } from "react";
import { Controller, FormProvider, useForm } from "react-hook-form";
import * as yup from "yup";
import {
  createExpenseEmployee,
  deleteExpenseEmployee,
  getExpenseAppartments,
  getExpenseChildren,
  getExpenseEmployeeByAmka,
  getExpenseEmployeeJobTypes,
  getExpenseTypes,
  getTransTypes,
  updateExpenseEmployee,
} from "src/apis/controllers/expenses";
import CustomSimpleAccordion from "qubCommon/Accordions/CustomSimpleAccordion";
import CurrencyInput from "src/qubCommon/Inputs/CurrencyInput";
import {
  CssTextField,
  DateInput,
  Dropdown,
  TextInput,
} from "src/qubCommon/Inputs";
import {
  getResponseMessage,
  getResponsePayload,
  isErrorResponse,
} from "./headerPayload";
import ExpenseEmployeeDialog from "./ExpenseEmployeeDialog";

const normalizeMode = (mode) => mode?.toString?.().trim().toLowerCase() ?? "";

const isRequiredMode = (mode) => normalizeMode(mode) === "required";

const isHiddenMode = (mode) => normalizeMode(mode) === "hidden";

const isVisibleMode = (mode) => !!mode && !isHiddenMode(mode);

const isTrueValue = (value) =>
  value === true || value?.toString?.().trim?.().toLowerCase?.() === "true";

const normalizeAmka = (value) => value?.replace?.(/\D/g, "").slice(0, 11) ?? "";

const normalizeAfm = (value) => value?.replace?.(/\D/g, "").slice(0, 9) ?? "";

const DetailDatePickerButton = forwardRef(({ onClick }, ref) => (
  <IconButton
    aria-label="Άνοιγμα ημερολογίου"
    onClick={onClick}
    ref={ref}
    tabIndex={-1}
  >
    <DateRangeSharp />
  </IconButton>
));

DetailDatePickerButton.displayName = "DetailDatePickerButton";

const EMPLOYEE_CREATE_OPTION_ID = "__create_employee__";
const EMPLOYEE_CREATE_OPTION = {
  id: EMPLOYEE_CREATE_OPTION_ID,
  isCreateOption: true,
  fullName: "Δημιουργία Νέου εργαζομένου",
};

const isEmployeeCreateOption = (option) => option?.isCreateOption === true;

const emptyToNull = (value) => {
  const nextValue = value?.toString?.().trim?.() ?? "";
  return nextValue === "" ? null : nextValue;
};

const hasSelectedValue = (value) =>
  value !== undefined && value !== null && value?.toString?.().trim?.() !== "";

const normalizeDate = (value) => {
  if (!value) return null;
  if (value instanceof Date) return value.toISOString().slice(0, 10);

  const nextValue = value.toString();
  return nextValue.includes("T") ? nextValue.split("T")[0] : nextValue;
};

const parseAmount = (value) => {
  if (value === undefined || value === null || value === "") return null;
  if (typeof value === "number") return Number.isFinite(value) ? value : null;

  let normalized = value
    .toString()
    .replace(/\s/g, "")
    .replace("€", "")
    .trim();

  if (normalized.includes(",") && normalized.includes(".")) {
    normalized = normalized.replace(/\./g, "").replace(",", ".");
  } else {
    normalized = normalized.replace(",", ".");
  }

  const amount = Number(normalized);
  return Number.isFinite(amount) ? amount : null;
};

const amountFormatter = new Intl.NumberFormat("el-GR", {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

const formatAmount = (value) => amountFormatter.format(Number(value ?? 0));

const formatAmountForInput = (value) => {
  const amount = parseAmount(value);
  return amount === null ? "" : formatAmount(amount);
};

const amountValidation = ({ requiredMessage, min, minMessage }) =>
  yup
    .mixed()
    .test("amount-required", requiredMessage, (value) => parseAmount(value) !== null)
    .test("amount-min", minMessage, (value) => {
      const amount = parseAmount(value);
      if (amount === null) return true;
      return amount >= min;
    });

const vatAmountValidation = yup
  .mixed()
  .test("vat-required", "Απαιτείται ΦΠΑ.", (value, context) => {
    if (isTrueValue(context?.parent?.hideVat)) return true;
    return parseAmount(value) !== null;
  })
  .test("vat-min", "Ο ΦΠΑ δεν μπορεί να είναι αρνητικός.", (value, context) => {
    if (isTrueValue(context?.parent?.hideVat)) return true;
    const amount = parseAmount(value);
    if (amount === null) return true;
    return amount >= 0;
  });

const requiredByMode = (modeField, message) =>
  yup
    .mixed()
    .nullable()
    .test("required-by-mode", message, (value, context) => {
      const mode = context?.parent?.[modeField];
      if (!isRequiredMode(mode)) return true;
      if (value instanceof Date) return true;
      if (typeof value === "object" && value !== null) return true;
      return value !== undefined && value !== null && value !== "";
    });

const schema = yup.object({
  expenseTypeId: yup
    .mixed()
    .test(
      "expense-type-required",
      "Απαιτείται επιλογή είδους δαπάνης.",
      (value) => value !== undefined && value !== null && value !== ""
    ),
  expenseTypeIsSelectable: yup.boolean().test(
    "expense-type-selectable",
    "Επιλέξτε διαθέσιμο είδος δαπάνης.",
    (value) => value === true
  ),
  netValue: amountValidation({
    requiredMessage: "Απαιτείται αξία.",
    min: 0.01,
    minMessage: "Η αξία πρέπει να είναι μεγαλύτερη από 0.",
  }),
  vatValue: vatAmountValidation,
  appartmentMode: yup.string().nullable(),
  childMode: yup.string().nullable(),
  employeeMode: yup.string().nullable(),
  dateFromMode: yup.string().nullable(),
  dateToMode: yup.string().nullable(),
  hideVat: yup.mixed().nullable(),
  appartmentId: requiredByMode(
    "appartmentMode",
    "Απαιτείται επιλογή διαμερίσματος."
  ),
  child: requiredByMode("childMode", "Απαιτείται επιλογή παιδιού."),
  employeeId: requiredByMode(
    "employeeMode",
    "Απαιτείται αναζήτηση και επιλογή εργαζομένου."
  ),
  dateFrom: requiredByMode("dateFromMode", "Απαιτείται ημερομηνία από."),
  dateTo: requiredByMode("dateToMode", "Απαιτείται ημερομηνία έως."),
  comments: yup.string().nullable(),
  remarks: yup.string().nullable(),
});

const initialFormValues = {
  expenseTypeId: "",
  expenseTypeIsSelectable: false,
  netValue: "0,00",
  vatValue: "0,00",
  appartmentId: "",
  child: null,
  employeeId: null,
  employeeAmka: "",
  employeeSelectedAmka: "",
  employeeName: "",
  appartmentMode: "",
  childMode: "",
  employeeMode: "",
  dateFromMode: "",
  dateToMode: "",
  hideVat: false,
  dateFrom: "",
  dateTo: "",
  comments: "",
  remarks: "",
};


const valueOrDefault = (value, defaultValue = "") =>
  value !== undefined && value !== null && value !== "" ? value : defaultValue;

const getInitialValues = (row, defaults = {}) => ({
  ...initialFormValues,
  expenseTypeId: row?.expenseTypeId ?? "",
  expenseTypeIsSelectable: !!row?.expenseTypeId,
  netValue: formatAmountForInput(row?.netValue ?? 0),
  vatValue: formatAmountForInput(row?.vatValue ?? 0),
  appartmentId: row?.appartmentId ?? "",
  child: row?.childId
    ? {
      id: row.childId,
      amka: row.childAmka,
      fullName: row.childFullName,
    }
    : null,
  employeeId: row?.employeeId ?? null,
  employeeAmka: row?.employeeAmka ?? "",
  employeeSelectedAmka: row?.employeeAmka ?? "",
  employeeName: row?.employeeFullName ?? "",
  dateFrom: valueOrDefault(row?.dateFrom, defaults.dateFrom),
  dateTo: valueOrDefault(row?.dateTo, defaults.dateTo),
  comments: row?.expComments ?? "",
  remarks: row?.expRemarks ?? "",
});

const getExpenseTypeOptions = (res) => {
  const payload = getResponsePayload(res);
  return Array.isArray(payload) ? payload : [];
};

const getTransTypeOptions = (res) => {
  const payload = getResponsePayload(res);
  return Array.isArray(payload) ? payload : [];
};

const getEmployeeJobTypeOptions = (res) => {
  const payload = getResponsePayload(res);
  return Array.isArray(payload) ? payload : [];
};

const getModeConfig = (transType) => ({
  appartmentMode: transType?.appartmenMode ?? transType?.appartmentMode ?? "",
  childMode: transType?.childMode ?? "",
  employeeMode: transType?.employeeMode ?? "",
  dateFromMode: transType?.dateFromMode ?? "",
  dateToMode: transType?.dateToMode ?? "",
  hideVat: transType?.hideVat ?? transType?.hidevat ?? transType?.HideVat ?? false,
});

const getChildLabel = (child) => {
  if (!child) return "";

  return [child.appartmentAbbr, child.amka, child.fullName]
    .filter(Boolean)
    .join(" - ");
};

const getEmployeeName = (employee) => {
  if (!employee) return "";

  return (
    employee.fullName ||
    [employee.lastName, employee.firstName].filter(Boolean).join(" ") ||
    employee.email ||
    ""
  );
};

const getEmployeeOptionLabel = (employee) => {
  if (!employee) return "";
  if (isEmployeeCreateOption(employee)) return EMPLOYEE_CREATE_OPTION.fullName;

  return [getEmployeeName(employee), employee.jobTitleName]
    .filter(Boolean)
    .join(" - ");
};

const getEmployeeOptions = (res) => {
  const payload = getResponsePayload(res);

  if (Array.isArray(payload?.employees)) return payload.employees;
  if (Array.isArray(payload)) return payload;
  if (payload?.employee) return [payload.employee];

  return [];
};

const getInitialEmployee = (row) => {
  if (!row?.employeeId) return null;

  return {
    id: row.employeeId,
    afm: row.employeeAfm ?? "",
    amka: row.employeeAmka ?? "",
    firstName: row.employeeFirstName,
    lastName: row.employeeLastName,
    fullName: row.employeeFullName,
    jobTitleId: row.employeeJobTitleId,
    jobTitleName: row.employeeJobTitleName,
    email: row.employeeEmail,
    comments: row.employeeComments,
    isFromExpenses: row.employeeIsFromExpenses,
    canEdit: row.employeeCanEdit,
    canDelete: row.employeeCanDelete,
    bundleIds: row.employeeBundleIds,
  };
};

const inputSx = {
  "& .MuiInputBase-input": {
    fontSize: 15,
  },
};

const formSurfaceSx = {
  width: "100%",
  maxWidth: 760,
  mx: "auto",
  display: "grid",
  gridTemplateColumns: {
    xs: "1fr",
    sm: "max-content minmax(0, 1fr) max-content minmax(0, 1fr)",
  },
  columnGap: 1,
  rowGap: 0.75,
  alignItems: "center",
};

const detailFormRowSx = {
  display: { xs: "grid", sm: "contents" },
  gridTemplateColumns: "1fr",
  mb: { xs: 0.75, sm: 0 },
};

const detailLabelSx = {
  px: 1,
  color: "#111",
  fontSize: 16,
  fontStyle: "italic",
  lineHeight: 1.1,
  textAlign: { xs: "left", sm: "right" },
  whiteSpace: { sm: "nowrap" },
};

const detailFieldSx = {
  minWidth: 0,
  px: 1,
  py: 0.35,
  "& > *": {
    width: "100%",
  },
};

const DetailFormRow = ({ label, children, minHeight = 24 }) => (
  <Box
    sx={detailFormRowSx}
  >
    <Box sx={{ ...detailLabelSx, alignSelf: "center", gridColumn: { sm: "1" }, minHeight }}>
      {label}:
    </Box>
    <Box
      sx={{
        ...detailFieldSx,
        alignSelf: "center",
        gridColumn: { sm: "2 / -1" },
        minHeight,
      }}
    >
      {children}
    </Box>
  </Box>
);

const DetailFormPairRow = ({
  leftLabel,
  leftChildren,
  rightLabel,
  rightChildren,
  minHeight = 24,
}) => (
  <Box sx={detailFormRowSx}>
    <Box
      sx={{
        ...detailLabelSx,
        alignSelf: "center",
        gridColumn: { sm: "1" },
        minHeight,
      }}
    >
      {leftLabel}:
    </Box>
    <Box
      sx={{
        ...detailFieldSx,
        alignSelf: "center",
        gridColumn: { sm: "2" },
        minHeight,
      }}
    >
      {leftChildren}
    </Box>
    <Box
      sx={{
        ...detailLabelSx,
        gridColumn: { sm: "3" },
        minHeight,
      }}
    >
      {rightLabel}:
    </Box>
    <Box
      sx={{
        ...detailFieldSx,
        alignSelf: "center",
        gridColumn: { sm: "4" },
        minHeight,
      }}
    >
      {rightChildren}
    </Box>
  </Box>
);

const ReadOnlyValue = ({ value, muted = false }) => (
  <Box
    sx={{
      minHeight: 34,
      display: "flex",
      alignItems: "center",
      justifyContent: 'flex-end',
      px: 1.25,
      borderRadius: "0.25rem",
      backgroundColor: muted ? "#d0d0d0" : "#f4f7fa",
      color: "#111827",
      fontSize: 15,
      fontWeight: 600,
    }}
  >
    {value ?? "-"}
  </Box>
);

const ExpenseDetailDialog = ({
  open,
  mode = "create",
  applicationId,
  transHeaderId,
  transTypeId,
  defaultDateFrom,
  defaultDateTo,
  settings,
  initialValues,
  isSaving = false,
  title,
  onCancel,
  onSave,
}) => {
  const form = useForm({
    resolver: yupResolver(schema),
    defaultValues: initialFormValues,
  });
  const {
    clearErrors,
    control,
    formState: { errors },
    handleSubmit,
    reset,
    setValue,
    watch,
  } = form;

  const [transTypeOptions, setTransTypeOptions] = useState([]);
  const [expenseTypeOptions, setExpenseTypeOptions] = useState([]);
  const [employeeJobTypes, setEmployeeJobTypes] = useState([]);
  const [appartmentOptions, setAppartmentOptions] = useState([]);
  const [childOptions, setChildOptions] = useState([]);
  const [childSearch, setChildSearch] = useState("");
  const [employeeOptions, setEmployeeOptions] = useState([]);
  const [employeeSearch, setEmployeeSearch] = useState("");
  const [employeeMessage, setEmployeeMessage] = useState(null);
  const [metadataError, setMetadataError] = useState(null);
  const [isMetadataLoading, setIsMetadataLoading] = useState(false);
  const [hasLoadedMetadata, setHasLoadedMetadata] = useState(false);
  const [isFormInitialized, setIsFormInitialized] = useState(false);
  const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);
  const [isModeConfigApplied, setIsModeConfigApplied] = useState(false);
  const [isAppartmentsLoading, setIsAppartmentsLoading] = useState(false);
  const [isChildrenLoading, setIsChildrenLoading] = useState(false);
  const [isEmployeeLoading, setIsEmployeeLoading] = useState(false);
  const [isEmployeeSaving, setIsEmployeeSaving] = useState(false);
  const [isEmployeeDeleting, setIsEmployeeDeleting] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [employeeDialogState, setEmployeeDialogState] = useState({
    open: false,
    employee: null,
    initialAfm: "",
    initialAmka: "",
  });

  const expenseTypeId = watch("expenseTypeId");
  const netValue = watch("netValue");
  const vatValue = watch("vatValue");
  const appartmentMode = watch("appartmentMode");
  const childMode = watch("childMode");
  const employeeMode = watch("employeeMode");
  const dateFromMode = watch("dateFromMode");
  const dateToMode = watch("dateToMode");
  const hideVat = watch("hideVat");
  const dateFrom = watch("dateFrom");
  const dateTo = watch("dateTo");
  const selectedChild = watch("child");
  const employeeAmka = watch("employeeAmka");
  const employeeName = watch("employeeName");

  const isEditMode = mode === "edit";
  const selectedTransType = useMemo(
    () =>
      transTypeOptions.find(
        (option) => option.id?.toString() === transTypeId?.toString()
      ),
    [transTypeId, transTypeOptions]
  );
  const effectiveModeConfig = useMemo(() => {
    if (selectedTransType) return getModeConfig(selectedTransType);

    return {
      appartmentMode,
      childMode,
      employeeMode,
      dateFromMode,
      dateToMode,
      hideVat,
    };
  }, [
    appartmentMode,
    childMode,
    dateFromMode,
    dateToMode,
    employeeMode,
    hideVat,
    selectedTransType,
  ]);
  const selectedExpenseType = useMemo(
    () =>
      expenseTypeOptions.find(
        (option) => option.id?.toString() === expenseTypeId?.toString()
      ),
    [expenseTypeId, expenseTypeOptions]
  );
  const expenseTypeDropdownOptions = useMemo(
    () =>
      expenseTypeOptions.map((option) => ({
        ...option,
        value: option.id ?? "",
        label: option.abbr ?? option.name ?? "",
        isDisabled: option.isSelectable !== true,
      })),
    [expenseTypeOptions]
  );
  const appartmentDropdownOptions = useMemo(
    () =>
      appartmentOptions.map((option) => ({
        ...option,
        value: option.id,
        label: option.descr ?? option.abbr ?? option.name,
      })),
    [appartmentOptions]
  );
  const currentAppartmentMode = effectiveModeConfig.appartmentMode;
  const currentChildMode = effectiveModeConfig.childMode;
  const currentEmployeeMode = effectiveModeConfig.employeeMode;
  const currentDateFromMode = effectiveModeConfig.dateFromMode;
  const currentDateToMode = effectiveModeConfig.dateToMode;
  const currentHideVat = isTrueValue(effectiveModeConfig.hideVat);
  const visibleAppartment = isVisibleMode(currentAppartmentMode);
  const visibleChild = isVisibleMode(currentChildMode);
  const visibleEmployee = isVisibleMode(currentEmployeeMode);
  const visibleDateFrom = isVisibleMode(currentDateFromMode);
  const visibleDateTo = isVisibleMode(currentDateToMode);
  const visibleVat = !currentHideVat;
  const hasAppliedModeConfig =
    isModeConfigApplied &&
    appartmentMode === currentAppartmentMode &&
    childMode === currentChildMode &&
    employeeMode === currentEmployeeMode &&
    dateFromMode === currentDateFromMode &&
    dateToMode === currentDateToMode &&
    isTrueValue(hideVat) === currentHideVat;
  const parsedNetValue = parseAmount(netValue);
  const parsedVatValue = visibleVat ? parseAmount(vatValue) : 0;
  const totalAmount =
    parsedNetValue !== null || parsedVatValue !== null
      ? (parsedNetValue ?? 0) + (parsedVatValue ?? 0)
      : 0;
  const formattedTotalAmount = formatAmount(totalAmount);
  const canReview = settings?.review?.can === true;
  const shouldShowApprovedAmount =
    isEditMode && settings?.showApprovedAmount === true;
  const shouldShowStatus =
    isEditMode &&
    (canReview ||
      !!initialValues?.expStatusAbbr ||
      !!initialValues?.expStatusId ||
      !!initialValues?.statusDate ||
      !!initialValues?.statusComments);
  const isDialogReady =
    isFormInitialized &&
    hasLoadedMetadata &&
    (!!metadataError || (hasAppliedModeConfig && !!selectedTransType));
  const canSave =
    isDialogReady && !isSaving && !isMetadataLoading && !metadataError;
  const getDetailDatePickerProps = (fieldValue) => ({
    popperPlacement: "bottom-end",
    popperClassName: "expense-detail-date-popper",
    customInput: <DetailDatePickerButton />,
    onCalendarOpen: () => setIsDatePickerOpen(true),
    onCalendarClose: () => setIsDatePickerOpen(false),
    ...(hasSelectedValue(fieldValue) ? {} : { selected: null }),
    popperModifiers: [
      {
        name: "preventOverflow",
        options: {
          boundary: "viewport",
          padding: 8,
        },
      },
    ],
  });

  const handleAmountBlur = (fieldName) => (event) => {
    setValue(fieldName, formatAmountForInput(event.target.value), {
      shouldDirty: true,
      shouldValidate: true,
    });
  };

  useEffect(() => {
    if (!open) {
      setIsFormInitialized(false);
      setIsDatePickerOpen(false);
      setIsModeConfigApplied(false);
      return;
    }

    const nextValues = getInitialValues(initialValues, {
      dateFrom: defaultDateFrom,
      dateTo: defaultDateTo,
    });

    const nextEmployee = getInitialEmployee(initialValues);

    reset(nextValues);
    setSelectedEmployee(nextEmployee);
    setChildSearch("");
    setEmployeeSearch("");
    setEmployeeOptions([]);
    setEmployeeMessage(null);
    setMetadataError(null);
    setEmployeeDialogState({
      open: false,
      employee: null,
      initialAfm: "",
      initialAmka: "",
    });
    setHasLoadedMetadata(false);
    setIsModeConfigApplied(false);
    setIsFormInitialized(true);
  }, [defaultDateFrom, defaultDateTo, initialValues, open, reset]);

  useEffect(() => {
    if (!open) return undefined;

    let isActive = true;

    const fetchMetadata = async () => {
      setIsMetadataLoading(true);
      setHasLoadedMetadata(false);
      setMetadataError(null);
      setIsModeConfigApplied(false);

      try {
        if (!transTypeId) {
          throw new Error(
            "Δεν βρέθηκε το είδος παραστατικού για τη φόρτωση ειδών δαπάνης."
          );
        }

        const [transTypesRes, expenseTypesRes, jobTypesRes] = await Promise.all([
          getTransTypes(false),
          getExpenseTypes(transTypeId),
          getExpenseEmployeeJobTypes(),
        ]);

        if (!isActive) return;

        if (isErrorResponse(transTypesRes)) {
          throw new Error(
            getResponseMessage(
              transTypesRes,
              "Δεν φορτώθηκαν τα είδη παραστατικού."
            )
          );
        }

        if (isErrorResponse(expenseTypesRes)) {
          throw new Error(
            getResponseMessage(
              expenseTypesRes,
              "Δεν φορτώθηκαν τα είδη δαπάνης."
            )
          );
        }

        setTransTypeOptions(getTransTypeOptions(transTypesRes));
        setExpenseTypeOptions(getExpenseTypeOptions(expenseTypesRes));
        if (isErrorResponse(jobTypesRes)) {
          setEmployeeJobTypes([]);
          enqueueSnackbar(
            getResponseMessage(
              jobTypesRes,
              "Δεν φορτώθηκαν οι ιδιότητες εργαζομένων."
            ),
            { variant: "warning" }
          );
        } else {
          setEmployeeJobTypes(getEmployeeJobTypeOptions(jobTypesRes));
        }
      } catch (error) {
        if (!isActive) return;

        setTransTypeOptions([]);
        setExpenseTypeOptions([]);
        setEmployeeJobTypes([]);
        setIsModeConfigApplied(false);
        setMetadataError(
          error?.message ?? "Δεν φορτώθηκαν τα στοιχεία της φόρμας δαπάνης."
        );
      } finally {
        if (isActive) {
          setIsMetadataLoading(false);
          setHasLoadedMetadata(true);
        }
      }
    };

    fetchMetadata();

    return () => {
      isActive = false;
    };
  }, [open, transTypeId]);

  useEffect(() => {
    if (!open || isMetadataLoading) return;

    setIsModeConfigApplied(false);

    if (!transTypeId || !selectedTransType) {
      setMetadataError(
        "Δεν βρέθηκαν οι κανόνες πεδίων για το είδος παραστατικού."
      );
      return;
    }

    setMetadataError(null);
    const modeConfig = getModeConfig(selectedTransType);
    Object.entries(modeConfig).forEach(([fieldName, value]) => {
      setValue(fieldName, value, { shouldDirty: false, shouldValidate: false });
    });

    if (isHiddenMode(modeConfig.appartmentMode)) {
      setValue("appartmentId", "", {
        shouldDirty: false,
        shouldValidate: false,
      });
      clearErrors("appartmentId");
    }
    if (isHiddenMode(modeConfig.childMode)) {
      setValue("child", null, { shouldDirty: false, shouldValidate: false });
      clearErrors("child");
    }
    if (isHiddenMode(modeConfig.employeeMode)) {
      setValue("employeeId", null, {
        shouldDirty: false,
        shouldValidate: false,
      });
      setValue("employeeAmka", "", {
        shouldDirty: false,
        shouldValidate: false,
      });
      setValue("employeeSelectedAmka", "", {
        shouldDirty: false,
        shouldValidate: false,
      });
      setValue("employeeName", "", {
        shouldDirty: false,
        shouldValidate: false,
      });
      setSelectedEmployee(null);
      setEmployeeSearch("");
      setEmployeeOptions([]);
      setEmployeeMessage(null);
      clearErrors("employeeId");
    }
    if (isHiddenMode(modeConfig.dateFromMode)) {
      setValue("dateFrom", "", { shouldDirty: false, shouldValidate: false });
      clearErrors("dateFrom");
    }
    if (isHiddenMode(modeConfig.dateToMode)) {
      setValue("dateTo", "", { shouldDirty: false, shouldValidate: false });
      clearErrors("dateTo");
    }
    if (isTrueValue(modeConfig.hideVat)) {
      setValue("vatValue", "0,00", {
        shouldDirty: false,
        shouldValidate: false,
      });
      clearErrors("vatValue");
    }

    setIsModeConfigApplied(true);
  }, [
    clearErrors,
    isMetadataLoading,
    open,
    selectedTransType,
    setValue,
    transTypeId,
  ]);

  useEffect(() => {
    if (!open || !expenseTypeId || expenseTypeOptions.length === 0) return;

    setValue("expenseTypeIsSelectable", selectedExpenseType?.isSelectable === true, {
      shouldDirty: false,
      shouldValidate: true,
    });
  }, [expenseTypeId, expenseTypeOptions, open, selectedExpenseType, setValue]);

  useEffect(() => {
    if (!open || !visibleAppartment || !applicationId || !transTypeId) {
      setAppartmentOptions([]);
      return undefined;
    }

    let isActive = true;

    const fetchAppartments = async () => {
      setIsAppartmentsLoading(true);

      try {
        const res = await getExpenseAppartments(applicationId, transTypeId);
        if (!isActive) return;

        if (isErrorResponse(res)) {
          throw new Error(
            getResponseMessage(res, "Δεν φορτώθηκαν τα διαμερίσματα.")
          );
        }

        const payload = getResponsePayload(res);
        setAppartmentOptions(Array.isArray(payload) ? payload : []);
      } catch (error) {
        if (!isActive) return;

        setAppartmentOptions([]);
        enqueueSnackbar(
          error?.message ?? "Δεν φορτώθηκαν τα διαμερίσματα.",
          { variant: "warning" }
        );
      } finally {
        if (isActive) {
          setIsAppartmentsLoading(false);
        }
      }
    };

    fetchAppartments();

    return () => {
      isActive = false;
    };
  }, [applicationId, open, transTypeId, visibleAppartment]);

  useEffect(() => {
    if (!open || !visibleChild || !applicationId || !transTypeId) {
      setChildOptions([]);
      return undefined;
    }

    let isActive = true;
    const timeoutId = setTimeout(async () => {
      setIsChildrenLoading(true);

      try {
        const res = await getExpenseChildren({
          applicationId,
          transTypeId,
          search: childSearch || undefined,
        });
        if (!isActive) return;

        if (isErrorResponse(res)) {
          throw new Error(
            getResponseMessage(res, "Δεν φορτώθηκαν τα παιδιά.")
          );
        }

        const payload = getResponsePayload(res);
        setChildOptions(Array.isArray(payload) ? payload : []);
      } catch (error) {
        if (!isActive) return;

        setChildOptions([]);
        enqueueSnackbar(error?.message ?? "Δεν φορτώθηκαν τα παιδιά.", {
          variant: "warning",
        });
      } finally {
        if (isActive) {
          setIsChildrenLoading(false);
        }
      }
    }, childSearch ? 350 : 0);

    return () => {
      isActive = false;
      clearTimeout(timeoutId);
    };
  }, [applicationId, childSearch, open, transTypeId, visibleChild]);

  const mergedChildOptions = useMemo(() => {
    if (!selectedChild?.id) return childOptions;

    const exists = childOptions.some(
      (option) => option.id?.toString() === selectedChild.id?.toString()
    );

    return exists ? childOptions : [selectedChild, ...childOptions];
  }, [childOptions, selectedChild]);

  useEffect(() => {
    if (!open || !visibleEmployee || !applicationId || !transTypeId) {
      setEmployeeOptions([]);
      return undefined;
    }

    let isActive = true;
    const search = employeeSearch?.trim?.() ?? "";

    const timeoutId = setTimeout(async () => {
      setIsEmployeeLoading(true);

      try {
        const res = await getExpenseEmployeeByAmka({
          applicationId,
          transTypeId,
          search: search || undefined,
        });
        if (!isActive) return;

        if (isErrorResponse(res)) {
          throw new Error(
            getResponseMessage(res, "Δεν φορτώθηκαν οι εργαζόμενοι.")
          );
        }

        const payload = getResponsePayload(res);
        const employees = getEmployeeOptions(res);
        setEmployeeOptions(employees);
        setEmployeeMessage(
          employees.length === 0 && payload?.message ? payload.message : null
        );
      } catch (error) {
        if (!isActive) return;

        setEmployeeOptions([]);
        setEmployeeMessage(
          error?.message ?? "Δεν φορτώθηκαν οι εργαζόμενοι."
        );
      } finally {
        if (isActive) {
          setIsEmployeeLoading(false);
        }
      }
    }, search ? 350 : 0);

    return () => {
      isActive = false;
      clearTimeout(timeoutId);
    };
  }, [applicationId, employeeSearch, open, transTypeId, visibleEmployee]);

  const mergedEmployeeOptions = useMemo(() => {
    const withCreateOption = (options) => [...options, EMPLOYEE_CREATE_OPTION];

    if (!selectedEmployee?.id) return withCreateOption(employeeOptions);

    const exists = employeeOptions.some(
      (option) => option.id?.toString() === selectedEmployee.id?.toString()
    );

    const options = exists ? employeeOptions : [selectedEmployee, ...employeeOptions];
    return withCreateOption(options);
  }, [employeeOptions, selectedEmployee]);

  const clearEmployeeValues = ({ keepSearch = "" } = {}) => {
    setSelectedEmployee(null);
    setValue("employeeId", null, { shouldDirty: true, shouldValidate: true });
    setValue("employeeAmka", normalizeAmka(keepSearch), {
      shouldDirty: true,
      shouldValidate: true,
    });
    setValue("employeeSelectedAmka", "", { shouldDirty: true });
    setValue("employeeName", "", { shouldDirty: true });
    setEmployeeSearch(keepSearch);
    clearErrors("employeeId");
  };

  const applyEmployee = (employee, fallbackAmka) => {
    const normalizedEmployeeAmka = normalizeAmka(employee?.amka ?? fallbackAmka);

    setSelectedEmployee(employee ?? null);
    setValue("employeeId", employee?.id ?? null, {
      shouldDirty: true,
      shouldValidate: true,
    });
    setValue("employeeAmka", normalizedEmployeeAmka, {
      shouldDirty: true,
      shouldValidate: true,
    });
    setValue("employeeSelectedAmka", normalizedEmployeeAmka, {
      shouldDirty: true,
    });
    setValue("employeeName", getEmployeeName(employee), { shouldDirty: true });
    setEmployeeSearch("");
    setEmployeeMessage(null);
    clearErrors("employeeId");
  };

  const handleOpenCreateEmployee = () => {
    const search = employeeSearch?.trim?.() ?? "";

    setEmployeeDialogState({
      open: true,
      employee: null,
      initialAfm: normalizeAfm(search),
      initialAmka: normalizeAmka(search),
    });
  };

  const handleOpenEditEmployee = () => {
    if (!selectedEmployee?.id) return;

    setEmployeeDialogState({
      open: true,
      employee: selectedEmployee,
      initialAfm: selectedEmployee?.afm ?? "",
      initialAmka: selectedEmployee?.amka ?? employeeAmka,
    });
  };

  const handleCancelEmployeeDialog = () => {
    setEmployeeDialogState({
      open: false,
      employee: null,
      initialAfm: "",
      initialAmka: "",
    });
  };

  const handleSelectExistingEmployee = (employee) => {
    if (!employee?.id) return;

    applyEmployee(employee, employee?.amka);
    setEmployeeDialogState({
      open: false,
      employee: null,
      initialAfm: "",
      initialAmka: "",
    });
    enqueueSnackbar("Ο εργαζόμενος επιλέχθηκε.", { variant: "success" });
  };

  const handleSaveEmployee = async (employeePayload) => {
    try {
      setIsEmployeeSaving(true);
      const res = employeePayload?.id
        ? await updateExpenseEmployee(employeePayload)
        : await createExpenseEmployee(employeePayload);

      if (isErrorResponse(res)) {
        enqueueSnackbar(
          getResponseMessage(res, "Δεν αποθηκεύτηκε ο εργαζόμενος."),
          { variant: "error" }
        );
        return;
      }

      const employee = getResponsePayload(res);

      if (!employee?.id) {
        enqueueSnackbar("Δεν επιστράφηκαν στοιχεία εργαζομένου.", {
          variant: "error",
        });
        return;
      }

      applyEmployee(employee, employeePayload?.amka);
      setEmployeeDialogState({
        open: false,
        employee: null,
        initialAfm: "",
        initialAmka: "",
      });
      enqueueSnackbar("Ο εργαζόμενος αποθηκεύτηκε.", { variant: "success" });
    } catch (error) {
      enqueueSnackbar(
        error?.message ?? "Προέκυψε σφάλμα κατά την αποθήκευση εργαζομένου.",
        { variant: "error" }
      );
    } finally {
      setIsEmployeeSaving(false);
    }
  };

  const handleDeleteEmployee = async (employee) => {
    if (!employee?.id) return;

    try {
      setIsEmployeeDeleting(true);
      const res = await deleteExpenseEmployee(employee.id);
      const payload = getResponsePayload(res);
      const message = getResponseMessage(res, "Δεν διαγράφηκε ο εργαζόμενος.");

      if (isErrorResponse(res) || payload?.deleted !== true) {
        enqueueSnackbar(message, { variant: "error" });
        return;
      }

      clearEmployeeValues();
      setEmployeeDialogState({
        open: false,
        employee: null,
        initialAfm: "",
        initialAmka: "",
      });
      enqueueSnackbar(message, { variant: "success" });
    } catch (error) {
      enqueueSnackbar(
        error?.message ?? "Προέκυψε σφάλμα κατά τη διαγραφή εργαζομένου.",
        { variant: "error" }
      );
    } finally {
      setIsEmployeeDeleting(false);
    }
  };

  const handleSave = (values) => {
    const payload = {
      ...(isEditMode ? { id: initialValues?.id } : {}),
      transHeaderId,
      expenseTypeId: Number(values.expenseTypeId),
      netValue: parseAmount(values.netValue),
      vatValue: visibleVat ? parseAmount(values.vatValue) : 0,
      appartmentId: visibleAppartment ? emptyToNull(values.appartmentId) : null,
      childId: visibleChild ? values.child?.id ?? null : null,
      employeeId: visibleEmployee ? values.employeeId ?? null : null,
      dateFrom: visibleDateFrom ? normalizeDate(values.dateFrom) : null,
      dateTo: visibleDateTo ? normalizeDate(values.dateTo) : null,
      comments: emptyToNull(values.comments),
      remarks: emptyToNull(values.remarks),
    };

    onSave?.(payload);
  };

  const canEditSelectedEmployee =
    selectedEmployee?.isFromExpenses === true &&
    selectedEmployee?.canEdit === true;

  return (
    <FormProvider {...form}>
      <GlobalStyles
        styles={{
          ".expense-detail-date-popper": {
            zIndex: "1500 !important",
          },
        }}
      />
      <Dialog
        open={open}
        onClose={onCancel}
        fullWidth
        maxWidth="md"
        disableEnforceFocus={isDatePickerOpen}
      >
        <DialogTitle variant="h4">
          {title ?? (isEditMode ? "Επεξεργασία Δαπάνης" : "Προσθήκη Δαπάνης")}
        </DialogTitle>
        <DialogContent sx={{ pt: 1 }}>
          {!isDialogReady ? (
            <Alert severity="info">Φόρτωση στοιχείων δαπάνης...</Alert>
          ) : (
            <Stack spacing={1.5}>
              {metadataError && <Alert severity="error">{metadataError}</Alert>}
              {isMetadataLoading && (
                <Alert severity="info">Φόρτωση επιλογών δαπάνης...</Alert>
              )}

              <Box sx={formSurfaceSx}>
                <DetailFormRow label="Είδος Δαπάνης">
                  <Dropdown
                    name="expenseTypeId"
                    label={null}
                    options={expenseTypeDropdownOptions}
                    valueName="value"
                    labelName="label"
                    placeholder="Επιλέξτε είδος δαπάνης"
                    disabled={isMetadataLoading}
                    optionDisabledName="isDisabled"
                    changeFn={() =>
                      clearErrors(["expenseTypeId", "expenseTypeIsSelectable"])
                    }
                    getOptionSx={(option) => ({
                      pl: 2 + Number(option.level ?? 0) * 2,
                      fontWeight: option.isHeader ? 800 : 500,
                    })}
                    optionKeyName="lookupKey"
                    sx={inputSx}
                  />
                </DetailFormRow>

                {visibleVat ? (
                  <DetailFormPairRow
                    leftLabel="Αξία(€)"
                    leftChildren={
                      <CurrencyInput
                        name="netValue"
                        label={null}
                        variant="standard"
                        inputAlign="right"
                        defaultFromProps={false}
                        blurFn={handleAmountBlur("netValue")}
                        sx={inputSx}
                      />
                    }
                    rightLabel="ΦΠΑ(€)"
                    rightChildren={
                      <CurrencyInput
                        name="vatValue"
                        label={null}
                        variant="standard"
                        inputAlign="right"
                        defaultFromProps={false}
                        blurFn={handleAmountBlur("vatValue")}
                        sx={inputSx}
                      />
                    }
                  />
                ) : (
                  <DetailFormRow label="Αξία(€)">
                    <CurrencyInput
                      name="netValue"
                      label={null}
                      variant="standard"
                      inputAlign="right"
                      defaultFromProps={false}
                      blurFn={handleAmountBlur("netValue")}
                      sx={inputSx}
                    />
                  </DetailFormRow>
                )}
                <DetailFormRow label="Σύνολο(€)">
                  <ReadOnlyValue value={formattedTotalAmount} muted />
                </DetailFormRow>

                {shouldShowApprovedAmount && (
                  <DetailFormRow label="Εγκεκριμένο">
                    <ReadOnlyValue value={formatAmount(initialValues?.approvedAmount)} />
                  </DetailFormRow>
                )}

                {shouldShowStatus && (
                  <>
                    <DetailFormRow label="Κατάσταση">
                      <ReadOnlyValue
                        value={
                          initialValues?.expStatusAbbr ??
                          initialValues?.statusAbbr ??
                          "-"
                        }
                        muted
                      />
                    </DetailFormRow>
                    {(initialValues?.statusDateFormatted ||
                      initialValues?.statusDate) && (
                        <DetailFormRow label="Ημ/νία Κατάστασης">
                          <ReadOnlyValue
                            value={
                              initialValues?.statusDateFormatted ??
                              initialValues?.statusDate
                            }
                            muted
                          />
                        </DetailFormRow>
                      )}
                    {initialValues?.statusComments && (
                      <DetailFormRow label="Σχόλια Κατάστασης" minHeight={64}>
                        <ReadOnlyValue value={initialValues.statusComments} muted />
                      </DetailFormRow>
                    )}
                  </>
                )}

                {visibleDateFrom && visibleDateTo && (
                  <DetailFormPairRow
                    leftLabel={`Από${isRequiredMode(currentDateFromMode) ? " *" : ""}`}
                    leftChildren={
                      <DateInput
                        name="dateFrom"
                        label={null}
                        variant="standard"
                        isFullWidth
                        sx={inputSx}
                        blurFn={() => clearErrors("dateFrom")}
                        datePickerProps={getDetailDatePickerProps(dateFrom)}
                      />
                    }
                    rightLabel={`Έως${isRequiredMode(currentDateToMode) ? " *" : ""}`}
                    rightChildren={
                      <DateInput
                        name="dateTo"
                        label={null}
                        variant="standard"
                        isFullWidth
                        sx={inputSx}
                        blurFn={() => clearErrors("dateTo")}
                        datePickerProps={getDetailDatePickerProps(dateTo)}
                      />
                    }
                  />
                )}

                {visibleDateFrom && !visibleDateTo && (
                  <DetailFormRow
                    label={`Από${isRequiredMode(currentDateFromMode) ? " *" : ""}`}
                  >
                    <DateInput
                      name="dateFrom"
                      label={null}
                      variant="standard"
                      isFullWidth
                      sx={inputSx}
                      blurFn={() => clearErrors("dateFrom")}
                      datePickerProps={getDetailDatePickerProps(dateFrom)}
                    />
                  </DetailFormRow>
                )}

                {visibleDateTo && !visibleDateFrom && (
                  <DetailFormRow
                    label={`Έως${isRequiredMode(currentDateToMode) ? " *" : ""}`}
                  >
                    <DateInput
                      name="dateTo"
                      label={null}
                      variant="standard"
                      isFullWidth
                      sx={inputSx}
                      blurFn={() => clearErrors("dateTo")}
                      datePickerProps={getDetailDatePickerProps(dateTo)}
                    />
                  </DetailFormRow>
                )}

                {visibleAppartment && (
                  <DetailFormRow
                    label={`Διαμέρισμα${isRequiredMode(currentAppartmentMode) ? " *" : ""
                      }`}
                  >
                    <Dropdown
                      name="appartmentId"
                      label={null}
                      options={appartmentDropdownOptions}
                      valueName="value"
                      labelName="label"
                      placeholder="Επιλέξτε διαμέρισμα"
                      disabled={isAppartmentsLoading}
                      sx={inputSx}
                    />
                  </DetailFormRow>
                )}

                {visibleChild && (
                  <DetailFormRow
                    label={`Ωφελούμενος${isRequiredMode(currentChildMode) ? " *" : ""}`}
                    minHeight={48}
                  >
                    <Controller
                      name="child"
                      control={control}
                      render={({ field }) => (
                        <Autocomplete
                          options={mergedChildOptions}
                          value={field.value}
                          filterOptions={(options) => options}
                          onInputChange={(_, value, reason) => {
                            if (reason === "input") setChildSearch(value);
                            if (reason === "clear") setChildSearch("");
                          }}
                          onChange={(_, value) => {
                            field.onChange(value);
                            setChildSearch("");
                            clearErrors("child");
                          }}
                          loading={isChildrenLoading}
                          isOptionEqualToValue={(option, value) =>
                            option.id?.toString() === value?.id?.toString()
                          }
                          getOptionLabel={getChildLabel}
                          renderOption={(props, option) => (
                            <Box component="li" {...props}>
                              <Stack direction="row" spacing={1} alignItems="center">
                                <Typography sx={{ fontSize: 13 }}>
                                  {getChildLabel(option)}
                                </Typography>
                                {option.isHistorical && (
                                  <Chip
                                    size="small"
                                    label="Ιστορικό"
                                    sx={{ height: 20, fontSize: 11 }}
                                  />
                                )}
                              </Stack>
                            </Box>
                          )}
                          renderInput={(params) => (
                            <CssTextField
                              {...params}
                              label={null}
                              variant="standard"
                              size="small"
                              error={!!errors.child}
                              helperText={errors.child?.message}
                              InputProps={{
                                ...params.InputProps,
                                disableUnderline: true,
                              }}
                              sx={inputSx}
                            />
                          )}
                        />
                      )}
                    />
                  </DetailFormRow>
                )}

                {visibleEmployee && (
                  <>
                    <DetailFormRow
                      label={`Εργαζόμενος${isRequiredMode(currentEmployeeMode) ? " *" : ""
                        }`}
                    >
                      <Box
                        sx={{
                          display: "grid",
                          gridTemplateColumns: {
                            xs: "1fr",
                            sm: canEditSelectedEmployee
                              ? "minmax(0, 1fr) 28px"
                              : "minmax(0, 1fr)",
                          },
                          gap: 1,
                          alignItems: "flex-start",
                        }}
                      >
                        <Controller
                          name="employeeId"
                          control={control}
                          render={({ field }) => (
                            <Autocomplete
                              options={mergedEmployeeOptions}
                              value={selectedEmployee}
                              loading={isEmployeeLoading}
                              filterOptions={(options) => options}
                              getOptionLabel={getEmployeeOptionLabel}
                              isOptionEqualToValue={(option, value) =>
                                option?.id?.toString() === value?.id?.toString()
                              }
                              loadingText="Φόρτωση εργαζομένων..."
                              noOptionsText="Δεν βρέθηκαν εργαζόμενοι"
                              onInputChange={(_, nextValue, reason) => {
                                if (reason === "input") {
                                  if (selectedEmployee) {
                                    clearEmployeeValues({ keepSearch: nextValue });
                                  } else {
                                    setEmployeeSearch(nextValue);
                                    setEmployeeMessage(null);
                                  }
                                }

                                if (reason === "clear") {
                                  clearEmployeeValues();
                                  field.onChange(null);
                                }
                              }}
                              onChange={(_, employee) => {
                                if (isEmployeeCreateOption(employee)) {
                                  handleOpenCreateEmployee();
                                  return;
                                }

                                if (employee) {
                                  applyEmployee(employee, employee?.amka);
                                  field.onChange(employee.id ?? null);
                                  return;
                                }

                                clearEmployeeValues();
                                field.onChange(null);
                              }}
                              renderOption={(props, option) => (
                                <Box component="li" {...props} key={option.id}>
                                  {isEmployeeCreateOption(option) ? (
                                    <Stack
                                      direction="row"
                                      spacing={1}
                                      alignItems="center"
                                      sx={{ color: "primary.main", fontWeight: 900 }}
                                    >
                                      <AddIcon fontSize="small" />
                                      <Typography sx={{ fontWeight: 900 }}>
                                        {EMPLOYEE_CREATE_OPTION.fullName}
                                      </Typography>
                                    </Stack>
                                  ) : (
                                    <Stack spacing={0.25}>
                                      <Typography sx={{ fontWeight: 800 }}>
                                        {getEmployeeName(option)}
                                      </Typography>
                                      <Typography
                                        sx={{ color: "#667085", fontSize: 12 }}
                                      >
                                        {[option.amka && `ΑΜΚΑ ${option.amka}`,
                                        option.afm && `ΑΦΜ ${option.afm}`,
                                        option.jobTitleName]
                                          .filter(Boolean)
                                          .join(" - ")}
                                      </Typography>
                                    </Stack>
                                  )}
                                </Box>
                              )}
                              renderInput={(params) => (
                                <CssTextField
                                  {...params}
                                  label={null}
                                  variant="standard"
                                  size="small"
                                  error={!!errors.employeeId}
                                  helperText={errors.employeeId?.message}
                                  InputProps={{
                                    ...params.InputProps,
                                    disableUnderline: true,
                                  }}
                                  sx={inputSx}
                                />
                              )}
                            />
                          )}
                        />
                        {canEditSelectedEmployee && (
                          <IconButton
                            aria-label="Επεξεργασία εργαζομένου"
                            color="primary"
                            size="small"
                            onClick={handleOpenEditEmployee}
                            sx={{ height: 36, width: 36 }}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        )}
                      </Box>
                    </DetailFormRow>
                    {selectedEmployee?.id && (
                      <DetailFormPairRow
                        leftLabel="ΑΜΚΑ"
                        leftChildren={
                          <CssTextField
                            label={null}
                            value={selectedEmployee?.amka ?? ""}
                            variant="standard"
                            size="small"
                            disabled
                            InputProps={{ disableUnderline: true }}
                            sx={inputSx}
                          />
                        }
                        rightLabel="ΑΦΜ"
                        rightChildren={
                          <CssTextField
                            label={null}
                            value={selectedEmployee?.afm ?? ""}
                            variant="standard"
                            size="small"
                            disabled
                            InputProps={{ disableUnderline: true }}
                            sx={inputSx}
                          />
                        }
                      />
                    )}
                    {employeeMessage && (
                      <Box
                        sx={{
                          gridColumn: { sm: "1 / -1" },
                          maxWidth: 520,
                          ml: "auto",
                          mr: 1,
                          mb: 1,
                        }}
                      >
                        <Alert
                          severity={
                            isRequiredMode(currentEmployeeMode) && !employeeName
                              ? "warning"
                              : "info"
                          }
                        >
                          {employeeMessage}
                        </Alert>
                      </Box>
                    )}
                  </>
                )}

                <Box sx={{ gridColumn: { sm: "1 / -1" }, mt: 1 }}>
                  <CustomSimpleAccordion
                    title={
                      <Typography sx={{ fontSize: 16, fontWeight: 800 }}>
                        Σχόλια - Παρατηρήσεις
                      </Typography>
                    }
                    content={
                      <Box sx={formSurfaceSx}>
                        <DetailFormRow label="Σχόλια">
                          <TextInput
                            name="comments"
                            label={null}
                            variant="standard"
                            sx={inputSx}
                          />
                        </DetailFormRow>
                        <DetailFormRow label="Παρατηρήσεις">
                          <TextInput
                            name="remarks"
                            label={null}
                            variant="standard"
                            sx={inputSx}
                          />
                        </DetailFormRow>
                      </Box>
                    }
                  />
                </Box>
              </Box>
            </Stack>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button variant="contained" color="error" onClick={onCancel}>
            Άκυρο
          </Button>
          <Button
            variant="contained"
            onClick={handleSubmit(handleSave)}
            disabled={!canSave}
          >
            Αποθήκευση
          </Button>
        </DialogActions>
      </Dialog>

      <ExpenseEmployeeDialog
        open={employeeDialogState.open}
        employee={employeeDialogState.employee}
        initialAfm={employeeDialogState.initialAfm}
        initialAmka={employeeDialogState.initialAmka || employeeAmka}
        applicationId={applicationId}
        transTypeId={transTypeId}
        jobTypes={employeeJobTypes}
        isSaving={isEmployeeSaving}
        isDeleting={isEmployeeDeleting}
        onCancel={handleCancelEmployeeDialog}
        onSave={handleSaveEmployee}
        onDelete={handleDeleteEmployee}
        onSelectExisting={handleSelectExistingEmployee}
      />
    </FormProvider>
  );
};

export default ExpenseDetailDialog;
